import { useQueryClient } from "@tanstack/react-query";
import { useDepublishInformation } from "features/OperationalInformation/useDepublishInformation";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { FailureMessageWithRetryButton } from "shared/components/feedback/FailureMessageWithRetryButton";
import {
  ButtonGroup,
  DropsButton,
  Flex,
  Text,
  Tooltip,
} from "shared/components/Spor";

type DepublishInfoProps = {
  uuid: string;
  disabled: boolean;
  queryKey: string[];
  depublishInternalMessage?: () => Promise<void>;
};

export const DepublishInformation: FC<DepublishInfoProps> = ({
  uuid,
  disabled,
  queryKey,
  depublishInternalMessage = () => {},
}) => {
  const queryClient = useQueryClient();
  const [askConfirmation, setAskConfirmation] = useState<boolean>(false);
  const { mutateAsync: depublish, status: depublishStatus } =
    useDepublishInformation();
  const { t } = useTranslation();

  const onSuccess = () => {
    setAskConfirmation(false);
    void queryClient.invalidateQueries({ queryKey });
  };

  if (depublishStatus === "error") {
    return (
      <FailureMessageWithRetryButton
        customMessage={t("Kunne ikke depublisere hendelsen")}
        retryFunction={() => {
          void depublish(uuid, { onSuccess }).then(depublishInternalMessage);
        }}
      />
    );
  }

  return askConfirmation ? (
    <Flex alignItems="center" gap="8px" minWidth="fit-content">
      <Text>{t("Er du sikker?")}</Text>
      <ButtonGroup>
        <DropsButton
          variant="primary"
          size="xs"
          onClick={() =>
            depublish(uuid, { onSuccess }).then(depublishInternalMessage)
          }
          loading={depublishStatus === "pending"}
        >
          {t("Ja, depubliser")}
        </DropsButton>
        <DropsButton
          variant="tertiary"
          size="xs"
          onClick={() => setAskConfirmation(false)}
        >
          {t("Avbryt")}
        </DropsButton>
      </ButtonGroup>
    </Flex>
  ) : (
    <Tooltip
      label={t("Kan ikke depublisere allerede usynlig hendelse")}
      hasArrow
      placement="left"
      isDisabled={!disabled}
    >
      <DropsButton
        variant="tertiary"
        size="xs"
        onClick={() => setAskConfirmation(true)}
        disabled={disabled}
      >
        {t("Depubliser")}
      </DropsButton>
    </Tooltip>
  );
};
